import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthenticationWrapper from '../../components/ui/AuthenticationWrapper';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn, user, loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const redirectTo = useMemo(() => {
    return (
      searchParams.get('redirect') ||
      location.state?.from ||
      '/main-dashboard'
    );
  }, [searchParams, location.state]);

  // ✅ Redirect si déjà connecté (attendre hydratation)
  useEffect(() => {
    if (authLoading) return;
    if (user) {
      navigate(redirectTo, { replace: true });
    }
  }, [authLoading, user, redirectTo, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = "L'adresse courriel est requise";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.email)) {
      newErrors.email = 'Veuillez entrer une adresse courriel valide';
    }

    if (!formData?.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      rememberMe: e?.target?.checked
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    const { data, error } = await signIn(formData?.email, formData?.password);

    if (error) {
      let errorMessage = 'Adresse courriel ou mot de passe incorrect. Veuillez réessayer.';

      // ✅ Option B: pas de flow "Email not confirmed"
      if (error?.message?.includes('Invalid login credentials')) {
        errorMessage = 'Adresse courriel ou mot de passe incorrect.';
      } else if (error?.message?.includes('Email')) {
        errorMessage = 'Veuillez vérifier votre adresse courriel.';
      } else if (error?.message) {
        errorMessage = error?.message;
      }

      setErrors({ general: errorMessage });
      setIsLoading(false);
      return;
    }

    // ✅ Pas de navigate forcé ici.
    // Le useEffect (user/authLoading) va rediriger quand la session sera hydratée.
    // Petit fallback au cas où l'état user tarde:
    if (data?.session?.user) {
      // On laisse le useEffect faire, mais si tu veux un fallback agressif:
      // navigate(redirectTo, { replace: true });
    }

    setIsLoading(false);
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    setErrors({});

    try {
      const callbackUrl = `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirectTo)}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl
        }
      });

      if (error) {
        setErrors({
          general: `Erreur lors de la connexion avec ${provider}. Veuillez réessayer.`
        });
        setIsLoading(false);
      }
      // sinon supabase redirige automatiquement
    } catch (err) {
      setErrors({
        general: 'Erreur réseau. Veuillez réessayer.'
      });
      setIsLoading(false);
    }
  };

  // Pendant l'hydratation, on peut afficher rien (ou un loader si tu veux)
  if (authLoading) {
    return null;
  }

  return (
    <AuthenticationWrapper
      title="Connexion"
      subtitle="Accédez à votre compte ImmoIQ pour analyser vos investissements immobiliers"
      showLogo={true}
    >
      <div style={{ paddingTop: 'var(--spacing-md)' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {errors?.general && (
            <div style={{
              padding: 'var(--spacing-sm)',
              background: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid var(--color-error)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-md)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--spacing-xs)'
            }}>
              <Icon name="AlertCircle" size={20} color="var(--color-error)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                color: 'var(--color-error)',
                margin: 0,
                lineHeight: 1.5
              }}>
                {errors?.general}
              </p>
            </div>
          )}

          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <Input
              type="email"
              name="email"
              label="Adresse courriel"
              placeholder="votre.email@exemple.ca"
              value={formData?.email}
              onChange={handleInputChange}
              error={errors?.email}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div style={{ marginBottom: 'var(--spacing-sm)' }}>
            <div style={{ position: 'relative' }}>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="Mot de passe"
                placeholder="Entrez votre mot de passe"
                value={formData?.password}
                onChange={handleInputChange}
                error={errors?.password}
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '38px',
                  background: 'none',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-muted-foreground)',
                  transition: 'color var(--transition-base)',
                  opacity: isLoading ? 0.5 : 1
                }}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
              </button>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-lg)',
            flexWrap: 'wrap',
            gap: 'var(--spacing-xs)'
          }}>
            <Checkbox
              label="Se souvenir de moi"
              checked={formData?.rememberMe}
              onChange={handleCheckboxChange}
              disabled={isLoading}
              size="sm"
            />
            <Link
              to="/forgot-password"
              style={{
                fontFamily: 'var(--font-caption)',
                fontSize: '0.875rem',
                color: 'var(--color-primary)',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'color var(--transition-base)',
                pointerEvents: isLoading ? 'none' : 'auto',
                opacity: isLoading ? 0.5 : 1
              }}
            >
              Mot de passe oublié?
            </Link>
          </div>

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            iconName="LogIn"
            iconPosition="right"
          >
            Se connecter
          </Button>

          <div style={{
            position: 'relative',
            margin: 'var(--spacing-lg) 0',
            textAlign: 'center'
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '1px',
              background: 'var(--color-border)'
            }} />
            <span style={{
              position: 'relative',
              display: 'inline-block',
              padding: '0 var(--spacing-sm)',
              background: 'var(--color-card)',
              fontFamily: 'var(--font-caption)',
              fontSize: '0.875rem',
              color: 'var(--color-muted-foreground)'
            }}>
              Ou continuer avec
            </span>
          </div>

          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={() => handleSocialLogin('google')}
              disabled={isLoading}
              iconName="Mail"
              iconPosition="left"
              fullWidth
            >
              Continuer avec Google
            </Button>
          </div>

          <div style={{
            textAlign: 'center',
            paddingTop: 'var(--spacing-md)',
            borderTop: '1px solid var(--color-border)'
          }}>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--color-muted-foreground)',
              margin: 0,
              marginBottom: 'var(--spacing-xs)'
            }}>
              Vous n'avez pas de compte?
            </p>

            <Link
              to={`/register?redirect=${encodeURIComponent(redirectTo)}`}
              state={{ from: redirectTo }}
            >
              <Button
                type="button"
                variant="ghost"
                size="default"
                disabled={isLoading}
                iconName="UserPlus"
                iconPosition="left"
              >
                Créer un compte gratuit
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </AuthenticationWrapper>
  );
};

export default Login;
